import type { ReactElement, ReactNode } from 'react';
import { logos } from '../assets';
import { cx, Icon, type IconName } from '../ui';
import { SetupSandboxBanner } from '../shell/SetupSandboxBanner';
import s from './OutsidePage.module.css';

/** A page outside the signed-in shell: the logo bar, one centred card, the motto. */
export function OutsidePage({
  title,
  children,
}: {
  /** The document title. */
  title: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div className={s.page}>
      <title>{`${title} · Areté MyRewards`}</title>
      <SetupSandboxBanner />
      <header className={s.bar}>
        <img src={logos.horizontalWhite} alt="Areté" width={127} height={34} className={s.logo} />
        <span className={s.app}>
          My<span className={s.appGold}>Rewards</span>
        </span>
      </header>
      <div className={s.rule} aria-hidden="true" />
      <main className={s.main}>{children}</main>
      <footer className={s.foot}>
        <span className={cx('eyebrow', s.motto)}>Discover. Develop. Deliver.</span>
      </footer>
    </div>
  );
}

/** The FR6 "can't show it" card: a red top rule, an icon, what's true, and one next step. */
export function OutsideCard({
  icon,
  tone = 'problem',
  title,
  children,
  actions,
}: {
  icon: IconName;
  tone?: 'problem' | 'info';
  title: string;
  children: ReactNode;
  actions?: ReactNode;
}): ReactElement {
  return (
    <section className={cx(s.card, tone === 'info' && s.cardInfo)} aria-labelledby="outside-title">
      <span className={s.icon} aria-hidden="true">
        <Icon name={icon} size={22} />
      </span>
      <div className={s.text}>
        <h1 id="outside-title" className={s.title}>
          {title}
        </h1>
        {children}
        {actions !== undefined && <div className={s.actions}>{actions}</div>}
      </div>
    </section>
  );
}
